import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';

import MainCard from 'components/MainCard';
import useApi from 'hooks/useApi';
import { diApi } from './api';
import type { PipelineSummary, RunOutcome, RunRow } from './types';
import OutcomeChip from './components/OutcomeChip';
import RunDetailDrawer from './components/RunDetailDrawer';
import { entityNoun, errorText, formatDateTime } from './components/format';

type OutcomeFilter = 'ALL' | RunOutcome;

const FILTERS: Array<{ value: OutcomeFilter; label: string }> = [
  { value: 'ALL', label: 'All' },
  { value: 'WOULD_CREATE', label: 'Would create' },
  { value: 'CREATED', label: 'Created' },
  { value: 'FLAGGED', label: 'Flagged' },
  { value: 'UPDATED', label: 'Updated' },
  { value: 'SKIPPED', label: 'Skipped' },
  { value: 'FAILED', label: 'Failed' }
];

const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 12 };

interface Props {
  clientUid: string;
  /** Pre-select this pipeline in the filter. */
  pipelineUid?: string | null;
}

export default function Runs({ clientUid, pipelineUid: initialPipeline = null }: Props) {
  const { showToast } = useApi();
  const toastRef = useRef(showToast);
  toastRef.current = showToast;

  const [outcome, setOutcome] = useState<OutcomeFilter>('ALL');
  const [pipelineFilter, setPipelineFilter] = useState<string>(initialPipeline ?? '');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(50);
  const [rows, setRows] = useState<RunRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pipelines, setPipelines] = useState<PipelineSummary[]>([]);
  const [openRun, setOpenRun] = useState<RunRow | null>(null);
  const requestId = useRef(0);

  useEffect(() => {
    let cancelled = false;
    diApi
      .getPipelines(clientUid)
      .then((res) => {
        if (!cancelled && res.success !== false) setPipelines(res.data ?? []);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [clientUid]);

  const load = useCallback(
    (silent = false) => {
      const ticket = ++requestId.current;
      if (!silent) setLoading(true);
      setError(null);
      diApi
        .getRuns(clientUid, {
          pipelineUid: pipelineFilter || undefined,
          // The API takes a comma list; "Skipped" also covers updates that changed no mapped field.
          outcome: outcome === 'ALL' ? undefined : outcome === 'SKIPPED' ? 'SKIPPED,NO_CHANGE' : outcome,
          page: page + 1,
          limit
        })
        .then((res) => {
          if (ticket !== requestId.current) return;
          if (res.success === false) {
            setError(res.message || 'Could not load runs.');
            return;
          }
          setRows(res.data?.rows ?? []);
          setTotal(res.data?.total ?? 0);
        })
        .catch((err) => {
          if (ticket === requestId.current) setError(errorText(err, 'Could not load runs.'));
        })
        .finally(() => {
          if (ticket === requestId.current) setLoading(false);
        });
    },
    [clientUid, pipelineFilter, outcome, page, limit]
  );

  useEffect(() => {
    load();
  }, [load]);

  const byUid = useMemo(() => new Map(pipelines.map((p) => [p.uid, p])), [pipelines]);

  // "Load" when every pipeline in view works on loads (the usual case), otherwise "Record".
  const noun = useMemo(() => {
    const inView = pipelineFilter ? pipelines.filter((p) => p.uid === pipelineFilter) : pipelines;
    const entities = Array.from(new Set(inView.map((p) => p.entity)));
    return entities.length === 1 ? entityNoun(entities[0]) : 'record';
  }, [pipelines, pipelineFilter]);
  const Noun = noun.charAt(0).toUpperCase() + noun.slice(1);

  // "High Value Deliveries runs in Shadow mode." — any shadow rows on screen get the banner.
  const shadowNames = useMemo(() => Array.from(new Set(rows.filter((r) => r.mode === 'SHADOW').map((r) => r.pipelineName))), [rows]);

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" spacing={2} flexWrap="wrap" useFlexGap>
        <Box>
          <Typography variant="h5">Runs</Typography>
          <Typography variant="body2" color="text.secondary">
            Every {noun} a pipeline looked at, and what it did. Kept for 90 days.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            select
            size="small"
            value={pipelineFilter}
            onChange={(e) => {
              setPipelineFilter(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 200 }}
            SelectProps={{ displayEmpty: true }}
            aria-label="Filter by pipeline"
          >
            <MenuItem value="">All pipelines</MenuItem>
            {pipelines.map((p) => (
              <MenuItem key={p.uid} value={p.uid}>
                {p.name}
              </MenuItem>
            ))}
          </TextField>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={outcome}
            onChange={(_e, v: OutcomeFilter | null) => {
              if (!v) return;
              setOutcome(v);
              setPage(0);
            }}
            aria-label="Filter runs by outcome"
          >
            {FILTERS.map((f) => (
              <ToggleButton key={f.value} value={f.value} sx={{ textTransform: 'none', px: 1.5 }}>
                {f.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      {shadowNames.length > 0 && (
        <Alert severity="info">
          <strong>{shadowNames.length === 1 ? `${shadowNames[0]} runs in Shadow mode.` : 'Some of these runs are Shadow runs.'}</strong>{' '}
          These outcomes show what would have happened. Nothing was created and nobody was notified.
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => load()}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <MainCard content={false}>
        <TableContainer sx={{ position: 'relative' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>{Noun}</TableCell>
                <TableCell>Pipeline</TableCell>
                <TableCell>Outcome</TableCell>
                <TableCell>Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow
                  key={r.uid}
                  hover
                  tabIndex={0}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setOpenRun(r)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setOpenRun(r);
                    }
                  }}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(r.createdAt)}</TableCell>
                  <TableCell>
                    <Box component="code" sx={mono}>
                      {r.recordId}
                    </Box>
                    {r.transactionKey && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {r.transactionKey}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {r.pipelineName}{' '}
                    <Typography component="span" variant="caption" color="text.secondary">
                      v{r.version}
                      {r.mode === 'SHADOW' ? ' · shadow' : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <OutcomeChip outcome={r.outcome} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{r.detail}</Typography>
                    {r.attempts > 1 && (
                      <Typography variant="caption" color="text.secondary">
                        Attempt {r.attempts}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!loading && rows.length === 0 && !error && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                      {outcome === 'ALL' && !pipelineFilter
                        ? 'No runs yet. Runs appear once a pipeline is published in Shadow or Live mode.'
                        : 'No runs match these filters.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={28} aria-label="Loading runs" />
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          rowsPerPage={limit}
          rowsPerPageOptions={[25, 50, 100]}
          onRowsPerPageChange={(e) => {
            setLimit(Number(e.target.value));
            setPage(0);
          }}
        />
      </MainCard>

      <RunDetailDrawer
        clientUid={clientUid}
        runUid={openRun?.uid ?? null}
        entity={openRun ? byUid.get(openRun.pipelineUid)?.entity ?? null : null}
        onClose={() => setOpenRun(null)}
        onRetried={() => load(true)}
      />
    </Stack>
  );
}
